import { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /**
   * When false the dialog is a HARD GATE: no Esc, no backdrop-click, no X — it
   * can only be dismissed from inside (e.g. the open-shift gate). Defaults true.
   */
  dismissible?: boolean;
  /** Roomier header/body padding for larger, form-heavy modals. Default false. */
  spacious?: boolean;
}

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
};

export function Dialog({ open, onClose, title, description, children, size = "md", dismissible = true, spacious = false }: DialogProps) {
  const pad = spacious ? "p-6 sm:p-7" : "p-5";
  useEffect(() => {
    if (!open || !dismissible) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose, dismissible]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        className={cn(
          "relative w-full mx-4 bg-white rounded-2xl z-10 flex flex-col max-h-[calc(100vh-3rem)]",
          sizeClasses[size]
        )}
        style={{ boxShadow: "0 0 2px rgba(0,0,0,0.10), 0 8px 32px rgba(0,0,0,0.18)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
      >
        {/* Header */}
        <div className={cn("flex items-start justify-between flex-shrink-0 border-b border-ds-ceramic", pad)}>
          <div>
            <h2
              id="dialog-title"
              className="text-base font-semibold text-ds-house"
              style={{ letterSpacing: "-0.01em" }}
            >
              {title}
            </h2>
            {description && (
              <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
            )}
          </div>
          {dismissible && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-full transition-colors ml-4 flex-shrink-0 text-muted-foreground hover:bg-ds-warm"
              aria-label="Close dialog"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className={cn("overflow-y-auto flex-1", pad)}>{children}</div>
      </div>
    </div>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "default";
  isLoading?: boolean;
}

export function ConfirmDialog({
  open, onClose, onConfirm, title, description,
  confirmLabel = "Confirm", cancelLabel = "Cancel",
  variant = "danger", isLoading = false,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title} size="sm">
      <p className="text-sm text-muted-foreground mb-5">{description}</p>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant === "danger" ? "destructive" : "default"}
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? "..." : confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
